import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { reduxForm, change, formValueSelector } from 'redux-form';
import {
  updateAddress,
  findAddresses,
  getUserAddress,
  resetAddressSearchResults,
  updateCountryCode,
  updateState,
  initializeAddressForm,
  koalaAnalyticsActions,
} from 'actions';
import AddressFormComponent from 'components/AddressForm/AddressFormComponent';
import { forms } from 'constants';
import {
  isValidPostCode,
  isValidAUPostCode,
  isValidZipCode,
  trimWhiteSpace,
} from 'utils/validation';
import appSelectors from 'selectors/app-selectors';
import { resetAppErrors } from 'actions/setters/updateAppErrors';
import { withTranslation } from 'react-i18next';

const fetchData = (dispatch, updateAddressForm) =>
  dispatch(getUserAddress(updateAddressForm));

const validate = (values, { countryCode, t }) => {
  const errors = {};
  if (!trimWhiteSpace(values.AddressLine1)) {
    errors.AddressLine1 = t([
      'addressForm.errors.AddressLine1Enter',
      'Please enter your address.',
    ]);
  } else if (values.AddressLine1.length > 100) {
    errors.AddressLine1 = t([
      'addressForm.errors.AddressLine1ToLong',
      'Address Line 1 is too long.',
    ]);
  }

  if (values.AddressLine2 && values.AddressLine2.length > 100) {
    errors.AddressLine2 = t([
      'addressForm.errors.AddressLine2ToLong',
      'Address Line 2 is too long.',
    ]);
  }

  if (!trimWhiteSpace(values.Town)) {
    errors.Town = t([
      'addressForm.errors.TownEnter',
      'Please enter your town or city.',
    ]);
  } else if (values.Town && values.Town.length > 50) {
    errors.Town = t([
      'addressForm.errors.TownToLong',
      'Please enter a town less than 50 characters.',
    ]);
  }

  if (!trimWhiteSpace(values.PostCode)) {
    if (values.CountryCode && values.CountryCode === 'AU') {
      errors.PostCode = t([
        'addressForm.errors.PostCodeEnter',
        'Please enter your Postcode.',
      ]);
    } else {
      errors.PostCode = t([
        'addressForm.errors.PostalCodeEnter',
        'Please enter your zip/postal code.',
      ]);
    }
  } else if (
    values.CountryCode &&
    values.CountryCode === 'US' &&
    !isValidZipCode(values.PostCode)
  ) {
    errors.PostCode = t([
      'addressForm.errors.ZipCodeInvalid',
      'Please enter a valid zip code.',
    ]);
  } else if (
    values.CountryCode &&
    values.CountryCode === 'GB' &&
    !isValidPostCode(values.PostCode)
  ) {
    errors.PostCode = t([
      'addressForm.errors.PostCodeInvalid',
      'Please enter a valid postcode.',
    ]);
  } else if (
    values.CountryCode &&
    values.CountryCode === 'AU' &&
    !isValidAUPostCode(values.PostCode)
  ) {
    errors.PostCode = t([
      'addressForm.errors.PostCodeInvalid',
      'Please enter a valid postcode.',
    ]);
  } else if (
    values.CountryCode &&
    values.CountryCode !== 'GB' &&
    countryCode !== 'US' &&
    values.PostCode &&
    values.PostCode.length > 10
  ) {
    errors.PostCode = t([
      'addressForm.errors.ZipCode/PostCodeInvalid',
      'Please enter a valid zip/postal code.',
    ]);
  }

  if (
    values.CountryCode &&
    (values.CountryCode === 'US' || values.CountryCode === 'AU') &&
    !values.County
  ) {
    errors.County = t(['addressForm.errors.State', 'Please select a state.']);
  }

  if (!trimWhiteSpace(values.CountryCode)) {
    errors.CountryCode = t([
      'addressForm.errors.CountryCode',
      'Please enter your country.',
    ]);
  }

  return errors;
};

const AddressFormContainer = reduxForm({
  form: forms.Address,
  validate,
})(AddressFormComponent);

const mapStateToProps = (state, ownProps) => {
  let initialValues;

  const {
    app: {
      userAddress: { data: userAddressData },
      findAddresses: findAddressesData,
      localeData,
    },
  } = state;

  const selector = formValueSelector(forms.Address);
  if (ownProps.location.search.length > 0) {
    const params = new URLSearchParams(ownProps.location.search);
    const address = {
      AddressLine1: params.get('AddressLine1'),
      AddressLine2: params.get('AddressLine2'),
      Town: params.get('Town'),
      PostCode: params.get('PostCode'),
      State: params.get('State'),
      Country: params.get('Country'),
    };
    initialValues = { ...userAddressData, ...address };
  } else {
    initialValues = userAddressData;
  }

  // we don't have the user's country,
  // so get it from the isoCountryCode set by X-Country-Code header to pre-fill form with
  let initialValuesWithIsoCountry = { ...initialValues };
  if (!initialValues.CountryCode) {
    const countryCode = state.app.localeData.isoCountryCode;
    initialValuesWithIsoCountry = {
      ...initialValuesWithIsoCountry,
      CountryCode: countryCode,
    };
  }

  return {
    initialValues: initialValuesWithIsoCountry,
    userAddress: userAddressData,
    findAddressResults: findAddressesData,
    localeData,
    hasParams: ownProps.location.search.length > 0,
    postCode: selector(state, 'PostCode'),
    status: {
      isFetching: appSelectors.isPersonalDetailsFetching(state),
      ...state.app.appErrors,
    },
  };
};

export const mapDispatchToProps = dispatch => ({
  submitHandler: values => dispatch(updateAddress(values)),
  findAddressesByPostCode: (PostCode, nextPage) => {
    dispatch(findAddresses(PostCode, nextPage));
  },
  changeFieldValue: (fieldName, value) => {
    dispatch(change(forms.Address, fieldName, value));
  },
  changeCountry: countryCode => {
    dispatch(updateCountryCode(countryCode));
  },
  changeState: stateCode => {
    dispatch(updateState(stateCode));
  },
  fetchData: updateAddressForm => {
    fetchData(dispatch, updateAddressForm);
  },
  resetAddressSearchResults: () => dispatch(resetAddressSearchResults()),
  initializeAddressForm: address => {
    dispatch(initializeAddressForm(address));
  },
  trackPageView: (subtype, pageSection) => {
    koalaAnalyticsActions.trackPageView(subtype, pageSection)(dispatch);
  },
  trackLinkClick: (subtype, pageSection, eventValue) => {
    koalaAnalyticsActions.trackLinkClick(
      subtype,
      pageSection,
      eventValue,
    )(dispatch);
  },
  resetAppErrors: () => {
    dispatch(resetAppErrors());
  },
});

const mergeProps = (stateProps, dispatchProps, ownProps) =>
  Object.assign({}, stateProps, dispatchProps, ownProps);

const AddressFormContainerConnected = withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps,
    mergeProps,
  )(withTranslation()(AddressFormContainer)),
);

AddressFormContainerConnected.fetchData = fetchData;

export default AddressFormContainerConnected;
